'use client';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { usePathname } from 'next/navigation';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

export interface BreadcrumbItem {
  label?: string;
  path?: string;
}

interface DynamicBreadcrumbsProps {
  items?: BreadcrumbItem[]; // optional overrides for middle crumbs
  currentPageLabel?: string; // optional override for last crumb
}

function humanize(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function DynamicBreadcrumbs({
  items = [],
  currentPageLabel,
}: DynamicBreadcrumbsProps): ReactElement {
  const pathname = usePathname() ?? '';
  const [h1Title, setH1Title] = useState<string | null>(null);

  // Only used as a *late* upgrade if no explicit label was given.
  useEffect(() => {
    if (currentPageLabel) return; // caller already told us, don't override
    const h1 = document.querySelector('h1');
    if (h1?.textContent) setH1Title(h1.textContent.trim());
  }, [currentPageLabel, pathname]);

  const siteUrl =
    process.env.NEXT_PUBLIC_HOMESITE_URL?.replace(/\/$/, '') ?? '';

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean); // e.g. ["resources","excise-tax","form-a"]

    // Auto-build a crumb per path segment, humanizing the slug as a fallback label.
    const auto: BreadcrumbItem[] = segments.map((seg, i) => ({
      label: humanize(seg),
      path: '/' + segments.slice(0, i + 1).join('/') + '/',
    }));

    if (auto.length === 0) return [];

    const first = auto[0]!; // e.g. "Resources"
    const last = auto[auto.length - 1]!; // current page, path always trusted from the URL

    // Middle crumbs: if the caller passed `items`, trust them AS-IS, in order.
    // No path matching — this is what previously caused silent mismatches.
    const middle = items.length > 0 ? items : auto.slice(1, -1);

    const merged: BreadcrumbItem[] = [
      first,
      ...middle,
      {
        ...last,
        label: currentPageLabel || h1Title || last.label,
        path: pathname, // always trust the real current URL, never override this
      },
    ];

    return merged;
  }, [pathname, items, currentPageLabel, h1Title]);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.path ? `${siteUrl}${item.path}` : undefined,
    })),
  };

  return (
    <>
      {breadcrumbItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema),
          }}
        />
      )}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        {breadcrumbItems.map((item, index) =>
          index === breadcrumbItems.length - 1 ? (
            <Typography
              key={`${item.label}-${index}`}
              color="text.primary"
              variant="body2"
              itemProp="headline"
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={`${item.label}-${index}`}
              href={item.path}
              sx={{ fontSize: 14, color: '#0202ff' }}
            >
              {item.label}
            </Link>
          )
        )}
      </Breadcrumbs>
    </>
  );
}
